import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, ErrorState, LoadingState, Text, TextField } from '@cinesend/ui-kit'
import { X } from '@phosphor-icons/react'
import useStore from '../../hooks/use_store'
import EditItemModal from './edit_item_modal'

const ACTIONS_WIDTH = 'w-24 shrink-0'

const LocalizableLabels = ({ collection, schema, cycleID, airlineID, disabled }) => {
  const [editingItem, setEditingItem] = useState(undefined)
  const [identifier, setIdentifier] = useState('')
  const [labelsByLanguage, setLabelsByLanguage] = useState({})
  const [saving, setSaving] = useState(false)
  const { data: languages, isLoading: isLangLoading } = useQuery([
    `languages?airlineId=${airlineID ?? ''}&schemaId=${schema?.id ?? ''}`,
  ])
  const {
    data: collectionValues,
    isLoading,
    error,
    refetch,
  } = useQuery([`collection-values`, `?collectionID=${collection.id}`, `&cycleID=${cycleID}`])
  const { createModel, deleteModel } = useStore()

  const items = (collectionValues?.filter((opt) => !opt.language_id) ?? []).sort((a, b) =>
    (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' })
  )
  const localizedValues = collectionValues?.filter((opt) => opt.language_id !== null) ?? []

  const addItem = async (event) => {
    event.preventDefault()
    const label = identifier.trim()
    if (!label || disabled || saving || !languages?.length) return
    setSaving(true)
    try {
      const created = await createModel.mutateAsync({
        endpoint: 'collection-values',
        data: { collection_id: collection.id, label },
      })
      const identifierId = created?.data?.collection_value?.id ?? created?.data?.id
      if (identifierId) {
        for (const language of languages) {
          const nextLabel = (labelsByLanguage[language.id] ?? '').trim()
          if (!nextLabel) continue
          await createModel.mutateAsync({
            endpoint: 'collection-values',
            data: {
              collection_id: collection.id,
              language_id: language.id,
              label: nextLabel,
              collection_value_id: identifierId,
            },
          })
        }
      }
      setIdentifier('')
      setLabelsByLanguage({})
    } finally {
      setSaving(false)
    }
  }

  if (error) {
    return (
      <ErrorState message={error.message ?? 'An error occurred. Please contact customer support.'} onRetry={refetch} />
    )
  }

  if (isLoading || isLangLoading) {
    return <LoadingState className='justify-center' />
  }

  const columns = (
    <>
      <Text as='span' variant='xs-medium' tone='secondary' className='min-w-0 flex-[1.2]'>
        Identifier
      </Text>
      {(languages ?? []).map((language) => (
        <Text as='span' key={language.id} variant='xs-medium' tone='secondary' className='min-w-0 flex-1'>
          {language.eng_description}
        </Text>
      ))}
    </>
  )

  return (
    <>
      <div className='-mx-2.5 flex items-center gap-2 pl-4 pr-2 pb-1 pt-2'>
        <div className='flex min-w-0 flex-1 items-center gap-2'>{columns}</div>
        <div className={ACTIONS_WIDTH} aria-hidden='true' />
      </div>
      {items.length === 0 ? (
        <Text as='p' variant='sm' tone='secondary' className='px-1.5 py-2'>
          No values yet.
        </Text>
      ) : (
        <ul className='m-0 -mx-2.5 flex list-none flex-col gap-1 p-0'>
          {items.map((item) => (
            <li key={item.id}>
              <div
                role={disabled ? undefined : 'button'}
                tabIndex={disabled ? undefined : 0}
                className={`flex items-center gap-2 rounded-lg py-2 pl-4 pr-2 ${
                  disabled
                    ? ''
                    : 'cursor-pointer hover:bg-[var(--cs-color-background-tertiary)] focus-visible:outline-none focus-visible:shadow-[var(--cs-focus-ring-default-sm)]'
                }`}
                onClick={disabled ? undefined : () => setEditingItem(item)}
                onKeyDown={
                  disabled
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setEditingItem(item)
                        }
                      }
                }
              >
                <div className='flex min-w-0 flex-1 items-center gap-2'>
                  <Text as='span' variant='sm-medium' className='min-w-0 flex-[1.2] truncate'>
                    {item.label || '—'}
                  </Text>
                  {(languages ?? []).map((language) => (
                    <Text as='span' key={language.id} variant='sm' tone='secondary' className='min-w-0 flex-1 truncate'>
                      {localizedValues.find(
                        (value) => value.language_id === language.id && value.collection_value_id === item.id
                      )?.label || '—'}
                    </Text>
                  ))}
                </div>
                <div className={`flex ${ACTIONS_WIDTH} justify-end`}>
                  <Button
                    variant='ghost'
                    iconOnly
                    leadingIcon={<X size={20} weight='regular' aria-hidden='true' />}
                    disabled={disabled || item.hidden_at}
                    onClick={(event) => {
                      event.stopPropagation()
                      deleteModel.mutate({
                        endpoint: 'collection-values',
                        id: item.id,
                        params: `?cycleID=${cycleID}`,
                        deleteNoun: 'value',
                        deleteName: item.label,
                      })
                    }}
                  >
                    Remove item
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!disabled && languages?.length ? (
        <form className='-mx-2.5 flex items-end gap-2 pl-4 pr-2' onSubmit={addItem}>
          <div className='flex min-w-0 flex-1 items-end gap-2'>
            <div className='min-w-0 flex-[1.2]'>
              <TextField
                label='Identifier'
                hideLabel
                placeholder='Identifier'
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </div>
            {languages.map((language) => (
              <div key={language.id} className='min-w-0 flex-1'>
                <TextField
                  label={language.eng_description}
                  hideLabel
                  placeholder={language.eng_description}
                  value={labelsByLanguage[language.id] ?? ''}
                  onChange={(e) =>
                    setLabelsByLanguage((prev) => ({
                      ...prev,
                      [language.id]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <div className={`flex ${ACTIONS_WIDTH} justify-end`}>
            <Button type='submit' disabled={!identifier.trim() || saving}>
              Add
            </Button>
          </div>
        </form>
      ) : null}
      {editingItem !== undefined && languages ? (
        <EditItemModal
          collection={collection}
          item={editingItem}
          languages={languages}
          localizedValues={localizedValues}
          onClose={() => setEditingItem(undefined)}
        />
      ) : null}
    </>
  )
}

export default LocalizableLabels
